import React, { useMemo, useState } from 'react';
import CertificateItem from './CertificateItem';
import { CERTIFICATES, certificates, CLOUD_CERTIFICATES } from './certificates';
import { createRoot } from 'react-dom/client';
import ThemeProvider from '../../shared/theme/ThemeProvider';
import { CertificateService } from './certificateService';
import { useLoading } from '../../helpers/useLoading';
import { get } from 'lodash';
import { Tracker } from '../../shared/tracker/Tracker';
import { AuthenticateCloudCertificateWithLogin } from './AuthenticateCloudCertificateWithLogin';
import { Button } from '../../components/UI-CHAKRA/Button';
import { Text } from '../../components/UI-CHAKRA/Text';
import { Spinner } from '../../components/UI-CHAKRA/Spinner';
import { Input } from '../../components/UI-CHAKRA/Input/Input';
import { FormControl } from '../../components/UI-CHAKRA/FormControl';
import { FormLabel } from '../../components/UI-CHAKRA/FormLabel';
import { FormErrorMessage } from '../../components/UI-CHAKRA/FormErrorMessage';
import BaseDialog from '../../components/Dialogs/BaseDialog';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import { AuthenticateCloudCertificateDialogWithPinAndOTP } from './AuthenticateCloudCertificateDialogWithPinAndOTP';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { logger } from '../../helpers/logger';
import { amplitudeModule } from '../../shared/amplitude';
import { toast } from 'react-toastify';

interface AutenticateDialogProps {
  onCancel(): void;
  onAuthenticated(): void;
  open: boolean;
  prescriptionId: number;
  provider: CLOUD_CERTIFICATES;
  prescriberId?: number;
}

export function AuthenticateDialog(props: AutenticateDialogProps) {
  const [open, setOpen] = useState(props.open);
  const [otp, setOtp] = useState<string>('');
  const [loading, runWithLoading] = useLoading();
  const [incorrectPassword, setIncorrectPassword] = useState(false);
  const { prescriberConfiguration } = usePrescriptionStore();
  const prescription = usePrescriptionStore((store) => store.prescription);
  const enableIncreasedSolutiOtpField = get(prescriberConfiguration, 'Features.enableIncreasedSolutiOtpField', false);

  const onConfirm = async () => {
    if (loading) return;
    runWithLoading(async () => {
      try {
        switch (props.provider) {
          case CERTIFICATES.SOLUTI: {
            if (!props.prescriberId) {
              toast.error("Prescritor não identificado");
              return
            }
            await CertificateService.authenticateSoluti(props.prescriberId, otp);
            break;
          }
          case CERTIFICATES.SAFEWEB: {
            await CertificateService.safeweb.authenticate(props.prescriptionId, otp);
            break;
          }
          case CERTIFICATES.DINAMO: {
            await CertificateService.dinamo.authenticate(props.prescriptionId, otp);
            break;
          }
        }

        if (incorrectPassword) {
          setIncorrectPassword(false);
        }

        setOpen(false);

        amplitudeModule.esignatureSuccess({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        });

        props.onAuthenticated();
      } catch (e) {
        const status = get(e, 'response.status', null);

        if (status === 412) {
          Tracker.trackEvent({
            event: 'Wrong Token',
          });
        }

        setIncorrectPassword(true);
        setOtp('');
      }
    });
  };

  const onCancel = () => {
    props.onCancel();
    setOpen(false);
  };

  const onChangeOtp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(event.target.value);
  };

  const onSubmitPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onConfirm();
  };

  const certificateItem = useMemo(() => {
    const certificate = certificates.find((certificate) => certificate.value === props.provider);
    return certificate ? <CertificateItem certificate={certificate} active={false} /> : null;
  }, [props.provider]);

  const certificateProps = useMemo(() => {
    switch (props.provider) {
      case CERTIFICATES.SOLUTI: {
        return {
          passwordType: 'text',
          passwordLength: enableIncreasedSolutiOtpField ? 10 : 6,
          inputLabel: 'Token BirdID',
          dialogTitle: 'Digite o token do seu certificado',
          dialogSubtitle: 'Seu token será válido por 7 dias',
        };
      }
      case CERTIFICATES.SAFEWEB: {
        return {
          passwordType: 'password',
          passwordLength: 255,
          inputLabel: 'Senha SafeID',
          dialogTitle: 'Digite a senha do seu certificado',
          dialogSubtitle: 'Sua senha terá validade por 7 dias',
        };
      }
      case CERTIFICATES.DINAMO: {
        return {
          passwordType: 'password',
          passwordLength: 255,
          inputLabel: 'Senha Dinamo',
          dialogTitle: 'Digite a senha do seu certificado',
          dialogSubtitle: '',
        };
      }
    }
  }, [props.provider, enableIncreasedSolutiOtpField]);

  return (
    <BaseDialog isOpen={open} onClose={onCancel}>
      <Text fontWeight={700} fontSize={'18px'} style={{ marginBottom: '8px' }}>
        {certificateProps && certificateProps.dialogTitle}
      </Text>
      <Text fontSize={'14px'}>{certificateProps && certificateProps.dialogSubtitle}</Text>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: 101, marginRight: 16 }}>{certificateItem}</div>
        <form onSubmit={onSubmitPassword}>
          <FormControl isInvalid={incorrectPassword}>
            <FormLabel>{certificateProps && certificateProps.inputLabel}</FormLabel>
            <Input
              w={'100%'}
              value={otp}
              onChange={onChangeOtp}
              maxLength={certificateProps && certificateProps.passwordLength}
              data-testid={'password'}
              type={certificateProps && certificateProps.passwordType}
              autoFocus
            />
            <FormErrorMessage>Senha incorreta</FormErrorMessage>
          </FormControl>
        </form>
      </div>

      <Flex padding={'24px'} gap={'12px'} justifyContent={'flex-end'}>
        <Button
          width={'121px'}
          onClick={onCancel}
          variant={'ghost'}
          bg={'gray.lightest'}
          color={'typography.darkest'}
          boxShadow={'none'}
          fontWeight={400}
          fontSize={'14px'}
        >
          Cancelar
        </Button>
        <Button width={'121px'} onClick={onConfirm} variant={'ghost'} backgroundColor={'info.main'} color={'#fff'}>
          {loading ? <Spinner color={'gray.light'} minH={'18px'} minW={'18px'} /> : 'Confirmar'}
        </Button>
      </Flex>
    </BaseDialog>
  );
}

interface AutenticateCloudCertificate {
  targetId: string;
  prescriptionId: number;
  provider: CLOUD_CERTIFICATES;
  needsPIN: boolean;
  needLogin?: boolean;
  needsOTP: boolean;
  prescriberId?: number
}

const authenticateCloudCertificate = ({
  targetId,
  prescriptionId,
  provider,
  needLogin = false,
  needsPIN,
  needsOTP,
  prescriberId,
}: AutenticateCloudCertificate ) => {
  const container = document.createElement('div');

  const target = document.getElementById(targetId);

  if (!target) return;

  target.append(container);

  const root = createRoot(container);

  const removeChild = () => {
    try {
      root.unmount();
      target.removeChild(container);
    } catch (e) {
      logger.error(e);
    }
  };

  return new Promise((resolve) => {
    const onCancel = () => {
      removeChild();
      resolve(false);
    };

    const onAuthenticated = () => {
      removeChild();
      resolve(true);
    };

    root.render(
      <ThemeProvider>
        {needLogin ? (
          <AuthenticateCloudCertificateWithLogin
            onCancel={onCancel}
            open={true}
            prescriptionId={prescriptionId}
            onAuthenticated={onAuthenticated}
            provider={provider}
            needsOTP={needsOTP}
          />
        ) : needsPIN ? (
          <AuthenticateCloudCertificateDialogWithPinAndOTP
            onCancel={onCancel}
            open={true}
            prescriptionId={prescriptionId}
            onAuthenticated={onAuthenticated}
            provider={provider}
            needsOTP={needsOTP}
          />
        ) : (
          <AuthenticateDialog
            onCancel={onCancel}
            open={true}
            prescriptionId={prescriptionId}
            onAuthenticated={onAuthenticated}
            provider={provider}
            prescriberId={prescriberId}
          />
        )}
      </ThemeProvider>
    );
  });
};

export const authenticateSoluti = ({
  targetId,
  prescriptionId,
  prescriberId,
}: Omit<AutenticateCloudCertificate, 'provider' | 'needsPIN' | 'needsOTP'> & { prescriberId: number }) => {
  return authenticateCloudCertificate({
    targetId,
    prescriptionId,
    provider: CERTIFICATES.SOLUTI,
    needsPIN: false,
    needsOTP: true,
    prescriberId,
  });
};

export const authenticateSafeweb = ({
  targetId,
  prescriptionId,
}: Omit<AutenticateCloudCertificate, 'provider' | 'needsPIN' | 'needsOTP'>) => {
  return authenticateCloudCertificate({
    targetId,
    prescriptionId,
    provider: CERTIFICATES.SAFEWEB,
    needsPIN: false,
    needsOTP: true,
  });
};

export const authenticateCertiSign = ({
  targetId,
  prescriptionId,
  needsOTP,
}: Omit<AutenticateCloudCertificate, 'provider' | 'needsPIN'>) => {
  return authenticateCloudCertificate({
    targetId,
    prescriptionId,
    provider: CERTIFICATES.CERTISIGN,
    needsPIN: true,
    needsOTP,
  });
};

export const authenticateDinamo = ({
  targetId,
  prescriptionId,
}: Omit<AutenticateCloudCertificate, 'provider' | 'needsPIN' | 'needsOTP'>) => {
  return authenticateCloudCertificate({
    targetId,
    prescriptionId,
    provider: CERTIFICATES.DINAMO,
    needsPIN: false,
    needsOTP: true,
  });
};

export const authenticateRemoteId = ({
  targetId,
  prescriptionId,
  needsOTP,
}: Omit<AutenticateCloudCertificate, 'provider' | 'needsPIN'>) => {
  return authenticateCloudCertificate({
    targetId,
    prescriptionId,
    provider: CERTIFICATES.REMOTE_ID,
    needsPIN: true,
    needsOTP,
    needLogin: true,
  });
};
